if (!customElements.get('homepage-hero-slideshow')) {
  customElements.define('homepage-hero-slideshow', class extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.setup());
    }

    setup() {
      if (this.controller || !this.isConnected) return;

      this.allSlides = [...this.querySelectorAll('[data-slide]')];
      if (this.allSlides.length < 2) return;

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileViewport = matchMedia('(max-width: 749px)');
      this.paused = this.motion.matches || Boolean(window.Shopify?.designMode);
      this.index = 0;
      this.visible = true;

      const controls = this.querySelector('.homepage-hero-slideshow__controls');
      if (controls) controls.hidden = true;
      this.progress = this.querySelector('[data-slide-progress]');
      this.progressNav = this.querySelector('[data-slide-progress-nav]');

      this.progressNav?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-slide-target]');
        if (!button || !this.progressNav.contains(button)) return;
        const target = Number(button.dataset.slideTarget);
        if (!Number.isInteger(target) || target < 0 || target >= this.slides.length) return;
        this.show(target);
      }, options);

      this.classList.add('is-ready');
      this.refreshSlides();

      this.addEventListener('focusin', () => {
        this.paused = true;
        this.schedule();
      }, options);

      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget && this.contains(event.relatedTarget)) return;
        this.paused = this.motion.matches || Boolean(window.Shopify?.designMode);
        this.schedule();
      }, options);

      this.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'touch') return;
        this.touch = { x: event.clientX, y: event.clientY };
      }, options);

      this.addEventListener('pointerup', (event) => {
        if (!this.touch) return;
        const dx = event.clientX - this.touch.x;
        const dy = event.clientY - this.touch.y;
        this.touch = null;
        if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
        this.show((this.index + (dx < 0 ? 1 : -1) + this.slides.length) % this.slides.length);
      }, options);

      this.addEventListener('pointercancel', () => {
        this.touch = null;
      }, options);

      this.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest('[data-slide]');
        const slideIndex = this.slides.indexOf(slide);
        if (slideIndex >= 0) {
          this.paused = true;
          this.show(slideIndex);
        }
      }, options);

      document.addEventListener('visibilitychange', () => this.schedule(), options);

      this.motion.addEventListener('change', () => {
        this.paused = this.motion.matches || Boolean(window.Shopify?.designMode);
        this.schedule();
      }, options);

      this.handleViewportChange = () => {
        this.refreshSlides();
        this.show(Math.min(this.index, this.slides.length - 1));
      };
      this.mobileViewport.addEventListener('change', this.handleViewportChange);

      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.schedule();
      });
      this.observer.observe(this);
      this.show(0);
    }

    refreshSlides() {
      this.slides = this.allSlides.filter(
        (slide) => !slide.hasAttribute('data-mobile-only') || this.mobileViewport.matches
      );

      this.allSlides.forEach((slide) => {
        const enabled = this.slides.includes(slide);
        slide.hidden = !enabled;
        if (!enabled) {
          slide.classList.remove('is-active');
          slide.inert = true;
          slide.setAttribute('aria-hidden', 'true');
        }
      });

      this.slides.forEach((slide, i) => {
        slide.setAttribute('aria-label', `${i + 1} / ${this.slides.length}`);
      });

      if (this.index >= this.slides.length) this.index = this.slides.length - 1;
      if (this.index < 0) this.index = 0;

      this.renderProgressNav();
      this.updateProgress();
    }

    show(index) {
      this.index = index;
      const activeSlide = this.slides[index];

      this.allSlides.forEach((slide) => {
        const enabled = this.slides.includes(slide);
        const active = enabled && slide === activeSlide;

        slide.hidden = !enabled;
        slide.classList.toggle('is-active', active);
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
      });

      this.updateProgress();
      this.schedule();
    }

    renderProgressNav() {
      if (!this.progressNav) return;

      this.progressNav.replaceChildren();
      this.progressNav.style.gridTemplateColumns = `repeat(${this.slides.length}, minmax(0, 1fr))`;

      this.slides.forEach((slide, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'homepage-hero-slideshow__progress-nav-button';
        button.dataset.slideTarget = String(index);
        button.setAttribute('aria-label', `Show slide ${index + 1} of ${this.slides.length}`);
        this.progressNav.appendChild(button);
      });
    }

    updateProgress() {
      if (!this.slides.length) return;

      const step = Math.min(this.index + 1, this.slides.length);
      const fill = (step / this.slides.length) * 100;
      this.style.setProperty('--sfny-progress-fill', `${fill}%`);

      if (this.progressNav) {
        [...this.progressNav.children].forEach((button, index) => {
          if (index === this.index) {
            button.setAttribute('aria-current', 'true');
          } else {
            button.removeAttribute('aria-current');
          }
        });
      }
    }

    schedule() {
      clearTimeout(this.timer);
      if (this.paused || !this.visible || document.hidden || this.slides.length < 2) return;
      this.timer = setTimeout(
        () => this.show((this.index + 1) % this.slides.length),
        Number(this.dataset.interval) || 4000
      );
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      clearTimeout(this.timer);
      this.observer?.disconnect();
      if (this.mobileViewport && this.handleViewportChange) {
        this.mobileViewport.removeEventListener('change', this.handleViewportChange);
      }
      this.controller?.abort();
      this.controller = null;
    }
  });
}
